import React from 'react';
import { withSitecoreContext } from '@sitecore-jss/sitecore-jss-react';
import { compose } from 'ramda';

import { withEditMode, withEditModeNotice } from '_containers/BaseComponent';
import { renderStartDate, renderEndDate } from './helpers';
import { dateIsDefined } from '_utils/data';
import { withBannerCurve } from '_utils/components/Corporate/BannerCurve';
import { StatusIcon } from '_utils/components/Corporate/StatusIcon';
import ScreenReader from '_utils/components/Corporate/ScreenReader';
import { BackgroundColors } from '_utils/components/Corporate/Section';
import { useTranslation } from 'react-i18next';

import {
  Container,
  InnerContainer,
  IconContainer,
  IconWrapper,
  Description,
  Title,
  Date,
  DateText,
  DateWrapper,
  DescriptionContainer,
  RichText,
  EditorDiv
} from './StyledCallout';
import PropTypes from 'prop-types';

const EditorText = compose(withEditModeNotice)(EditorDiv);

const Callout = (props) => {
  const [t] = useTranslation();
  const { sitecoreContext, rendering, editMode } = props;
  const routeFields = sitecoreContext?.route?.fields;
  const renderingFields = rendering?.fields?.data?.contextItem;

  const statusName = routeFields?.status?.fields?.name.value || null;
  const statusIcon = routeFields?.status?.fields?.icon.value || null;
  const statusDescription = routeFields?.statusDescription || null;
  const publicConsultationStartDate = renderingFields?.publicConsultationStartDate?.date || null;
  const publicConsultationEndDate = renderingFields?.publicConsultationEndDate?.date || null;

  let editorNotice = null;
  let dateTitle = null;

  if (
    publicConsultationStartDate &&
    publicConsultationEndDate &&
    dateIsDefined(publicConsultationStartDate) &&
    dateIsDefined(publicConsultationEndDate)
  ) {
    dateTitle = t('callout-public-consultation-date-title');
  }
  if (editMode && !routeFields?.statusDescription) {
    editorNotice = 'Status has no data source';
  }
  if (editMode && !statusDescription) {
    editorNotice = 'Status has no description';
  } else if (editMode && statusDescription) {
    editorNotice = 'Overrides status description';
  }

  if (statusName === null) {
    if (editMode) {
      return <EditorText noticeEnabled noticeLevel="warning" noticeMessage="Please set a Status" />;
    }

    return null;
  }

  return (
    <Container>
      <InnerContainer>
        <IconContainer>
          <IconWrapper $statusStyle={statusIcon}>
            <StatusIcon status={statusIcon} />
          </IconWrapper>
        </IconContainer>
        <DescriptionContainer>
          <Title role="text">
            {statusName}
          </Title>
          <Description className="no-visited-link">
            {editMode && (
              <RichText
                field={routeFields?.statusDescription}
                noticeEnabled={false}
                noticeLevel="info"
                noticeMessage={editorNotice}
              />
            )}
            {!editMode && <RichText field={routeFields?.statusDescription} />}
          </Description>
          {dateTitle && (
            <DateWrapper>
              <DateText>{dateTitle}</DateText>
              {publicConsultationStartDate && (
                <Date>{renderStartDate(publicConsultationStartDate)}</Date>
              )}
              {publicConsultationEndDate && (
                <Date>
                  {' '}
                  {t('callout-to-conjunction')} {renderEndDate(publicConsultationEndDate)}
                </Date>
              )}
            </DateWrapper>
          )}
        </DescriptionContainer>
      </InnerContainer>
    </Container>
  );
};

Callout.propTypes = {
  sitecoreContext: PropTypes.shape({
    route: PropTypes.shape({
      fields: PropTypes.shape({
        status: PropTypes.shape({
          fields: PropTypes.shape({
            name: PropTypes.shape({
              value: PropTypes.string,
            }),
            icon: PropTypes.shape({
              value: PropTypes.string,
            }),
          }),
        }),
        statusDescription: PropTypes.object,
      }),
    }),
  }),
  rendering: PropTypes.shape({
    fields: PropTypes.shape({
      data: PropTypes.shape({
        contextItem: PropTypes.shape({
          publicConsultationStartDate: PropTypes.shape({
            date: PropTypes.string,
          }),
          publicConsultationEndDate: PropTypes.shape({
            date: PropTypes.string,
          }),
        }),
      }),
    }),
  }),
  editMode: PropTypes.bool,
};

export default compose(
  withSitecoreContext(),
  withEditMode,
  withBannerCurve({
    backgroundColor: BackgroundColors.White,
    renderCurve: ({ sitecoreContext }) => {
      const statusName = sitecoreContext?.route?.fields?.status?.fields?.name.value || null;
      return statusName !== null;
    }
  })
)(Callout);
